import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Fragment } from "react";
import type { ICol } from "src/interfaces";
import { StyledTh } from "src/styled";

import {
  calculateHeaderDepth,
  countLeafColumns,
} from "./generateHeaders.utils";
import SingleHeader from "./SingleHeader";
interface Props {
  headers: ICol[];
  theme: any;
  omitThead?: boolean;
  isDynamic?: boolean;
  headerSize?: any;
  draggable?: boolean;
  cloneRows?: boolean;
  showAddCommentButton?: boolean;
  headerRef?: any;
  summary?: boolean;
  sectionIndex: number;
  NMEAButton?: boolean;
  entryIndex?: number;
  collapseable?: boolean;
  collapsed?: boolean;
  hasTitle?: boolean;
  pdfEntry?: boolean;
  onHeaderClick?: () => void;
  hideHeaders?: boolean;
  layout?: number[];
}
/**
 * Processes the headers to generate table rows based on the provided configuration.
 *
 * @param headers - An array of header objects defining the table structure.
 * @param theme - The theme object used for styling.
 * @param sectionIndex - The index of the current section.
 * @param headerSize - Optional. Specifies the size of the headers.
 * @param draggable - Optional. If true, includes a draggable column.
 * @param isDynamic - Optional. If true, includes dynamic row controls.
 * @param cloneRows - Optional. If true, includes a column for cloning rows.
 * @param showAddCommentButton - Optional. If true, includes a column for adding comments.
 * @param summary - Optional. If true, includes summary-specific headers.
 * @param NMEAButton - Optional. If true, includes a column for refreshing NMEA data.
 * @param pdfEntry - Optional. If true, includes PDF-specific headers.
 * @returns An array of JSX elements representing the table rows.
 */
const processHeaders = ({
  headers,
  theme,
  isDynamic = false,
  headerSize,
  draggable = false,
  cloneRows = false,
  showAddCommentButton = false,
  summary = false,
  sectionIndex,
  NMEAButton,
  entryIndex,
  collapseable = false,
  collapsed = false,
  hasTitle = false,
  onHeaderClick,
  pdfEntry = false,
  hideHeaders = false,
  layout,
}: Props): JSX.Element[] => {
  const maxDepth = calculateHeaderDepth(headers);

  const headersByLevel: ICol[][] = Array(maxDepth)
    .fill(null)
    .map(() => []);

  // Collapsed groups render nothing, matching Row, which omits their cells.
  const isCollapsedGroup = (header: ICol) =>
    header.type === "split" && Boolean(header.collapsed);

  const collectHeadersByLevel = (headers: ICol[], level: number) => {
    headers.forEach(header => {
      if (isCollapsedGroup(header)) return;
      headersByLevel[level].push(header);

      if (header.cols && header.cols.length > 0) {
        collectHeadersByLevel(header.cols, level + 1);
      }
    });
  };

  collectHeadersByLevel(headers, 0);

  const usedHeaderIds = new Set<string>();

  const rows: JSX.Element[] = [];

  for (let rowIndex = 0; rowIndex < maxDepth; rowIndex++) {
    const rowHeaders: JSX.Element[] = [];

    if (rowIndex === 0) {
      // Only show control buttons when not collapsed or when collapsed but has title
      const showControlButtons = !collapsed || hasTitle;

      if (draggable && showControlButtons) {
        rowHeaders.push(
          <StyledTh
            key="drag-header"
            rowSpan={maxDepth}
            style={{ width: "60px" }}>
            DRAG
          </StyledTh>,
        );
      }
    }

    if (rowIndex === 0) {
      headers.forEach((header, headerIndex) => {
        if (isCollapsedGroup(header)) return;
        const useColspan = header.cols ? countLeafColumns(header.cols) : 1;
        const useRowspan = header.cols ? 1 : maxDepth;

        rowHeaders.push(
          <SingleHeader
            key={`${header.name ?? ""}-${header.type ?? ""}-0-${headerIndex}`}
            headerObj={header}
            theme={theme}
            headerSize={headerSize}
            summary={summary}
            colspan={useColspan}
            rowspan={useRowspan}
            sectionIndex={sectionIndex}
            entryIndex={entryIndex}
            headerIndex={headerIndex}
            columnIndex={headerIndex}
            headersLength={headers.length}
            hideHeaders={hideHeaders}
            layout={layout}
          />,
        );
      });
    } else {
      const currentHeaders = headersByLevel[rowIndex];

      let headerIndex = 0;

      while (headerIndex < currentHeaders.length) {
        const header = currentHeaders[headerIndex];

        const headerId = `${header.name || ""}-${header.type || ""}-${rowIndex}-${headerIndex}`;

        if (!usedHeaderIds.has(headerId)) {
          usedHeaderIds.add(headerId);

          const useColspan = header.cols ? countLeafColumns(header.cols) : 1;
          const useRowspan = header.cols ? 1 : maxDepth - rowIndex;

          rowHeaders.push(
            <SingleHeader
              key={headerId}
              headerObj={header}
              theme={theme}
              headerSize={headerSize}
              summary={summary}
              colspan={useColspan}
              rowspan={useRowspan}
              sectionIndex={sectionIndex}
              entryIndex={entryIndex}
              headerIndex={headerIndex}
              columnIndex={headerIndex}
              headersLength={headers.length}
              pdfEntry={pdfEntry}
              hideHeaders={hideHeaders}
              layout={layout}
            />,
          );
        }

        headerIndex++;
      }
    }

    if (rowIndex === 0) {
      // Only show control buttons when not collapsed or when collapsed but has title
      const showControlButtons = !collapsed || hasTitle;

      if (NMEAButton && showControlButtons) {
        rowHeaders.push(
          <StyledTh
            key="refresh-header"
            rowSpan={maxDepth}
            style={{ width: "60px" }}>
            REFRESH
          </StyledTh>,
        );
      }
      if ((isDynamic || cloneRows) && showControlButtons) {
        rowHeaders.push(
          <StyledTh
            key="del-header"
            rowSpan={maxDepth}
            style={{ width: "60px" }}>
            DEL
          </StyledTh>,
        );
      }

      if (showAddCommentButton && showControlButtons) {
        rowHeaders.push(
          <StyledTh
            key="comment-header"
            rowSpan={maxDepth}
            style={{ width: "60px" }}>
            Comment
          </StyledTh>,
        );
      }
    }

    // Make first header row clickable for collapse if section has no title but is collapseable
    const shouldMakeRowClickable =
      rowIndex === 0 && collapseable && !hasTitle && onHeaderClick;

    if (collapsed && collapseable) {
      if (hasTitle) {
        return rows;
      } else if (rowIndex > 0) {
        return rows;
      }
    }

    rows.push(
      <tr
        key={`header-row-${rowIndex}`}
        onClick={shouldMakeRowClickable ? onHeaderClick : undefined}
        style={shouldMakeRowClickable ? { cursor: "pointer" } : {}}
        title={
          shouldMakeRowClickable
            ? collapsed
              ? "Click to expand section"
              : "Click to collapse section"
            : undefined
        }>
        {rowHeaders}
        {shouldMakeRowClickable && (
          <StyledTh
            style={{ width: "30px", textAlign: "center", padding: "4px" }}>
            {collapsed ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronUp size={16} />
            )}
          </StyledTh>
        )}
      </tr>,
    );
  }

  return rows;
};

/**
 * Generates the headers for a table based on the provided configuration.
 *
 * @param headers - An array of header objects defining the table structure.
 * @param theme - The theme object used for styling.
 * @param omitThead - Optional. If true, omits the `<thead>` element.
 * @param isDynamic - Optional. If true, includes dynamic row controls.
 * @param headerSize - Optional. Specifies the size of the headers.
 * @param draggable - Optional. If true, includes a draggable column.
 * @param cloneRows - Optional. If true, includes a column for cloning rows.
 * @param showAddCommentButton - Optional. If true, includes a column for adding comments.
 * @param headerRef - Optional. A reference to the `<thead>` element.
 * @param summary - Optional. If true, includes summary-specific headers.
 * @param sectionIndex - The index of the current section.
 * @param NMEAButton - Optional. If true, includes a column for refreshing NMEA data.
 * @returns A JSX element representing the table headers.
 */
export const generateHeaders = ({
  headers,
  headerRef,
  omitThead = false,
  entryIndex,
  pdfEntry,
  hideHeaders = false,
  ...props
}: Props) => {
  if (!headers) return;
  const headersArray = Array.isArray(headers[0]) ? headers[0] : headers;

  const rows = processHeaders({
    headers: headersArray,
    ...props,
    entryIndex,
    pdfEntry,
    hideHeaders,
  });

  const content = <Fragment>{rows}</Fragment>;

  if (omitThead) return content;
  return <thead ref={headerRef}>{content}</thead>;
};
